import { Canvas, useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { KeyboardControls } from "@react-three/drei";
import { Physics, useRapier } from "@react-three/rapier";
import React, { useCallback, useEffect, useRef, useState } from "react";
import Ecctrl from "ecctrl";
import useIsMobile from "../Hooks/useIsMobile";
import MobileJoystick, { MobileJumpButton } from "./MobileJoystick";
import PlayerRoom, { PlayerDecorations } from "./PlayerRoom";
import PlacedAvatars from "./PlacedAvatars";
import FixedElements from "./FixedElements";
import CenterRaycaster from "./CenterRayCaster";
import { useRoomStore, useUserInterfaceStore } from "../../store";
import { playAllPendingVideos } from "./VideoMaterial";

const MOBILE_FPS = 30;

function MobileFrameThrottle() {
  const { invalidate, setFrameloop } = useThree();

  useEffect(() => {
    setFrameloop('demand');
    const interval = setInterval(() => invalidate(), 1000 / MOBILE_FPS);
    return () => {
      clearInterval(interval);
      setFrameloop('always');
    };
  }, [invalidate, setFrameloop]);

  return null;
}

/**
 * Sets window.__MOGULPLEX_3D_READY__ after the Three.js renderer has produced
 * a minimum number of frames.  The screenshot worker waits on this flag so it
 * captures a fully-rendered scene instead of relying on a fixed delay.
 */
const READY_FRAME_THRESHOLD = 3;

function ReadinessMarker() {
  const frameCount = useRef(0);
  const signalled = useRef(false);

  useFrame(() => {
    if (signalled.current) return;
    frameCount.current++;
    if (frameCount.current >= READY_FRAME_THRESHOLD) {
      window.__MOGULPLEX_3D_READY__ = true;
      signalled.current = true;
      console.log('[ReadinessMarker] 3D scene ready');
    }
  });

  useEffect(() => {
    return () => {
      // Reset on unmount so a subsequent navigation starts clean
      window.__MOGULPLEX_3D_READY__ = false;
    };
  }, []);

  return null;
}

function PlayerScene() {
  const isMobile = useIsMobile();
  const [ready, setReady] = useState(false);
  const [playerSpawned, setPlayerSpawned] = useState(false);
  const cooldownRef = useRef(false);
  const scaleX = useRoomStore((s) => s.scaleX);
  const scaleZ = useRoomStore((s) => s.scaleZ);
  const roomDataLoaded = useUserInterfaceStore((s) => s.roomDataLoaded);

  // Wait for room data (config, decorations) to be fully loaded before enabling physics
  const roomReady = scaleX > 0 && scaleZ > 0 && roomDataLoaded;

  useEffect(() => {
    if (roomReady) {
      // Delay to ensure all data is loaded before rendering scene
      const timeout = setTimeout(() => setReady(true), 100);
      return () => clearTimeout(timeout);

    }
  }, [roomReady]);

  // WebGL context loss recovery
  const [contextLost, setContextLost] = useState(false);

  const handleCanvasCreated = useCallback((state) => {
    const canvas = state.gl.domElement;
    canvas.addEventListener("webglcontextlost", (event) => {
      event.preventDefault();
      setContextLost(true);
    });
    canvas.addEventListener("webglcontextrestored", () => {
      setContextLost(false);
    });
  }, []);

  // On first interaction, retry all videos that failed autoplay (iOS Low Power Mode)
  const hasTriggeredVideosRef = useRef(false);

  const handlePointerDown = (e) => {
    // Trigger pending videos on first interaction (must be in gesture call stack)
    if (!hasTriggeredVideosRef.current) {
      hasTriggeredVideosRef.current = true;
      playAllPendingVideos();
    }

    if (
      e.pointerType === "mouse" &&
      document.pointerLockElement === null &&
      !cooldownRef.current
    ) {
      e.target.requestPointerLock();
    }
  };

  const keyboardMap = [
    { name: "forward", keys: ["ArrowUp", "KeyW"] },
    { name: "backward", keys: ["ArrowDown", "KeyS"] },
    { name: "leftward", keys: ["ArrowLeft", "KeyA"] },
    { name: "rightward", keys: ["ArrowRight", "KeyD"] },
    { name: "jump", keys: ["Space"] },
    { name: "run", keys: ["Shift"] },
  ];

  return (
    <>
      {isMobile && ready && (
        <>
          <MobileJoystick />
          <MobileJumpButton />
        </>
      )}

      <Canvas
        camera={{ position: [0, 0.5, 0], fov: isMobile ? 100 : 60, near: 0.1, far: 1000 }}
        style={{ position: "relative", zIndex: 1, touchAction: "none" }}
        onPointerDown={handlePointerDown}
        gl={{ antialias: !isMobile, preserveDrawingBuffer: false }}
        dpr={[1, 2]}
        onCreated={handleCanvasCreated}
      >
        {ready && (
          <>
            {/* Decorations rendered outside Physics for reliable loading */}
            <PlayerDecorations />
            {/* Placed avatars share the same transform as PlayerRoom so saved coords align with the rendered room */}
            <group rotation={[0, (3 * Math.PI) / 4, 0]}>
              <group scale={0.125} position={[-scaleX / 25, 0, -scaleZ / 30]}>
                <PlacedAvatars />
              </group>
            </group>
            <Physics timeStep={1/60} interpolate>
              {/* Floor + doors mount first so trimesh collider can build quickly */}
              <PlayerRoom deferModels={!playerSpawned} />
              {/* Character only spawns after floor collider is verified via raycast */}
              <SpawnWhenFloorReady onSpawned={() => setPlayerSpawned(true)}>
                <KeyboardControls map={keyboardMap}>
                  <Ecctrl
                    camCollision={false}
                    camInitDis={-0.01}
                    camMinDis={-0.01}
                    camMaxDis={-0.01}
                    autoBalance={false}
                    camListenerTarget="document"
                    capsuleHalfHeight={1.3}
                    floatHeight={0.1}
                    camLerpMult={2000}
                    camFollowMult={2000}
                    turnVelMultiplier={1}
                    turnSpeed={100}
                    maxVelLimit={5}
                    linearVelocity={[0, 0, 0]}
                    jumpVel={6}
                    jumpVelDamping={0.3}
                    jumpCooldown={0.3}
                    mode="CameraBasedMovement"
                  />
                </KeyboardControls>
              </SpawnWhenFloorReady>
            </Physics>
          </>
        )}

        {ready && <ReadinessMarker />}
        {isMobile && <MobileFrameThrottle />}
        <CenterRaycaster isMobile={isMobile} />
        <FixedElements isEditing={false} />
        <HeadBob />
        <CameraClamp minHeight={0.65} />
      </Canvas>

      {contextLost && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 10,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0,0,0,0.85)",
            color: "white",
            fontFamily: "sans-serif",
          }}
        >
          <p style={{ fontSize: 18, marginBottom: 16 }}>
            Graphics context was lost.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: "12px 24px",
              fontSize: 16,
              borderRadius: 8,
              border: "none",
              background: "#3b82f6",
              color: "white",
              cursor: "pointer",
            }}
          >
            Reload Room
          </button>
        </div>
      )}
    </>
  );
}

export default PlayerScene;

/**
 * Waits for the room's floor collider to exist before rendering children (the character).
 * Casts a physics ray downward each frame — once it hits the floor, the collider is confirmed
 * ready and the character can safely spawn without falling through.
 * Falls back to spawning after 3 seconds in case the raycast never hits (broken room, etc).
 */
function SpawnWhenFloorReady({ children, onSpawned }) {
  const [floorReady, setFloorReady] = useState(false);
  const { world, rapier } = useRapier();
  const frameCountRef = useRef(0);

  useFrame(() => {
    if (floorReady) return;
    frameCountRef.current++;

    // Fallback: spawn after ~3 seconds regardless (180 frames at 60fps)
    if (frameCountRef.current > 180) {
      setFloorReady(true);
      onSpawned?.();
      return;
    }

    try {
      const ray = new rapier.Ray({ x: 0, y: 10, z: 0 }, { x: 0, y: -1, z: 0 });
      const hit = world.castRay(ray, 20, true);
      if (hit) {
        setFloorReady(true);
        onSpawned?.();
      }
    } catch (e) {
      // Physics world not fully initialized yet, try next frame
    }
  });

  if (!floorReady) return null;
  return <>{children}</>;
}

/**
 * Subtle walking head-bob. Mounted AFTER Ecctrl (and before CameraClamp) so it runs
 * later in the frame loop and can offset the camera height Ecctrl just wrote.
 *
 * Design:
 *  - Gait speed is derived from the camera's horizontal (x/z) movement — no Ecctrl
 *    internals needed. We never touch x/z ourselves, so the measurement stays clean.
 *  - Bob is a pure vertical translation (sin(phase) * amplitude). Because we only move y
 *    and leave camera rotation untouched, your gaze stays aimed at the same far-off point,
 *    so the bob reads as the body rising/falling rather than the view tilting.
 *  - Finish-the-step: when you stop, we don't just kill the bob wherever it is. We keep
 *    advancing the phase to the NEXT trough of the sine (sin = -1, i.e. the foot planted /
 *    step completed), settle there, and only then relax the amplitude back up to neutral.
 */
function HeadBob({
  maxAmplitude = 0.042, // peak vertical offset in world units — keep it subtle
  stepFrequency = 2.7, // radians of phase per unit of gait speed per second
  accelTau = 0.1, // amplitude ramp-up time constant when you start moving
  releaseTau = 0.15, // amplitude relax-to-neutral time constant once the step is finished
  maxSpeed = 5, // matches Ecctrl maxVelLimit — speed that maps to full amplitude
  moveThreshold = 0.2, // below this measured speed we treat input as released
} = {}) {
  const { camera } = useThree();
  const prev = useRef(new THREE.Vector3());
  const initialized = useRef(false);
  const phase = useRef(0);
  const amplitude = useRef(0);
  const wasWalking = useRef(false);
  const finishing = useRef(false); // mid-stride, walking the phase to the next trough
  const settleTarget = useRef(0); // phase value of that trough

  // Brisk-but-natural cadence used to complete the final step (rad/s).
  const settleRate = stepFrequency * maxSpeed * 0.8;
  const TWO_PI = Math.PI * 2;
  const TROUGH = (3 * Math.PI) / 2; // phase where sin() bottoms out

  useFrame((_, delta) => {
    const dt = Math.min(Math.max(delta, 1e-4), 0.1); // clamp to survive tab-stalls
    const pos = camera.position;

    // Measure horizontal speed from how far the camera moved since last frame.
    if (!initialized.current) {
      prev.current.copy(pos);
      initialized.current = true;
      return;
    }
    const dx = pos.x - prev.current.x;
    const dz = pos.z - prev.current.z;
    prev.current.copy(pos);
    const measured = Math.sqrt(dx * dx + dz * dz) / dt;
    const walking = measured > moveThreshold;

    // Falling edge (just stopped): lock in the next trough so we finish the step there.
    if (wasWalking.current && !walking && amplitude.current > 0.0001) {
      const k = Math.ceil((phase.current - TROUGH) / TWO_PI);
      const next = TROUGH + TWO_PI * k;
      if (next > phase.current + 1e-4) {
        finishing.current = true;
        settleTarget.current = next;
      }
    }
    wasWalking.current = walking;

    if (walking) {
      // Walking: phase tracks footfalls, amplitude eases toward the speed-scaled target.
      finishing.current = false;
      phase.current += measured * stepFrequency * dt;
      const targetAmp =
        THREE.MathUtils.clamp(measured / maxSpeed, 0, 1) * maxAmplitude;
      amplitude.current += (targetAmp - amplitude.current) * (1 - Math.exp(-dt / accelTau));
    } else if (finishing.current) {
      // Finish the step: keep stepping at a fixed cadence until we hit the next trough,
      // holding amplitude so the final dip stays visible.
      phase.current += settleRate * dt;
      if (phase.current >= settleTarget.current) {
        phase.current = settleTarget.current;
        finishing.current = false;
      }
    } else if (amplitude.current > 0.0001) {
      // Step completed (sitting at the trough): relax the head back up to neutral.
      amplitude.current += (0 - amplitude.current) * (1 - Math.exp(-dt / releaseTau));
    } else {
      // Fully at rest. Begin the next stride from a trough so the first move pushes up.
      amplitude.current = 0;
      phase.current = TROUGH;
    }

    // Pure vertical offset on top of the height Ecctrl already set this frame.
    pos.y += Math.sin(phase.current) * amplitude.current;
    // Priority 0.5: run AFTER Ecctrl's camera update (priority 0) so our bob isn't
    // overwritten, but BEFORE OutlineEffect's composer render (priority 1) so the bobbed
    // camera is what actually gets drawn. (Auto-render is already off — the composer owns it.)
  }, 0.5);

  return null;
}

function CameraClamp({ minHeight = 0.6 }) {
  const { camera } = useThree();

  useFrame(() => {
    if (camera.position.y < minHeight) {
      camera.position.y = minHeight;
    }
  });

  return null;
}
